import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, CheckCircle2, Upload } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchVehicles, submitApplication, publicUpload, formatApiErrorDetail, validateReferral, zar, whatsappLink } from "@/api";

const STEP_TITLES = [
  "Vehicle & Programme",
  "Personal & Driver",
  "Income & Affordability",
  "Banking & Declaration",
];

const INITIAL = {
  vehicle: { make: "", model: "", year_range: "", max_price: "", term: "48", ownership_deposit: "", security_deposit: "", max_monthly: "", collection_date: "", alternative_ok: "yes" },
  personal: { title: "Mr", first_names: "", surname: "", id_number: "", dob: "", nationality: "South African", mobile: "", email: "", address: "", period_at_address: "", licence_number: "", licence_code: "B", licence_expiry: "", commute_km: "", parking: "secure yard" },
  income: { employment_type: "employed", employer: "", job_title: "", employment_start: "", net_salary: "", other_income: "", business_name: "", business_turnover: "", monthly_drawings: "", expenses_rent: "", expenses_utilities: "", expenses_food: "", expenses_transport: "", expenses_other: "" },
  banking: { bank_name: "", account_holder: "", account_type: "cheque", account_number: "", branch_code: "", debit_day: "25" },
  documents: {},
  declarations: { private_use: false, no_ehailing: false, tracker_consent: false, popia_consent: false, truthful: false },
};

const Field = ({ label, id, testid, type = "text", required, value, onChange, placeholder, options, min }) => (
  <div>
    <label className="input-label" htmlFor={id}>{label}{required ? " *" : ""}</label>
    {options ? (
      <select id={id} data-testid={testid} className="input-field" value={value} onChange={onChange}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input id={id} data-testid={testid} type={type} min={min} required={required} className="input-field" value={value} onChange={onChange} placeholder={placeholder} />
    )}
  </div>
);

const stepMotion = {
  initial: { opacity: 0, x: 40 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
  transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
};

export default function Apply() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(INITIAL);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  useEffect(() => {
    fetchVehicles().then(setVehicles).catch(() => {});
    const saved = sessionStorage.getItem("compcar_prequal");
    if (saved) {
      try {
        const pq = JSON.parse(saved);
        setForm((f) => ({
          ...f,
          personal: { ...f.personal, email: pq.email || "", mobile: pq.mobile || "", first_names: pq.full_name || "" },
          income: { ...f.income, employment_type: pq.employment_type || "employed", net_salary: pq.net_income || "", expenses_other: pq.monthly_expenses || "" },
          vehicle: { ...f.vehicle, max_monthly: pq.max_affordable_payment || "", ownership_deposit: pq.ownership_deposit || "", term: String(pq.preferred_term || 48) },
        }));
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => {
    const slug = params.get("vehicle");
    if (!slug || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.slug === slug);
    if (v) {
      setForm((f) => ({
        ...f,
        vehicle: { ...f.vehicle, make: v.make, model: v.model, year_range: String(v.year), max_price: String(v.price) },
      }));
    }
  }, [params, vehicles]);

  const setSection = (section) => (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));
  };

  const v = form.vehicle, p = form.personal, inc = form.income, b = form.banking, d = form.declarations;
  const sv = setSection("vehicle"), sp = setSection("personal"), si = setSection("income"), sb = setSection("banking"), sd = setSection("declarations");

  const [docUploading, setDocUploading] = useState("");
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    const code = (params.get("ref") || "").trim().toUpperCase();
    if (!code) return;
    validateReferral(code)
      .then((res) => {
        setReferral({ code, name: res.referrer_first_name });
        toast.success(`Referral code applied — ${res.referrer_first_name} sent you`);
      })
      .catch(() => toast.error("That referral code isn't valid"));
  }, [params]);
  const handleDoc = (key) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(key);
    try {
      const res = await publicUpload(file);
      setForm((f) => ({ ...f, documents: { ...f.documents, [key]: res.path } }));
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDocUploading("");
    }
  };

  const totals = useMemo(() => {
    const income = inc.employment_type === "employed"
      ? Number(inc.net_salary || 0) + Number(inc.other_income || 0)
      : Number(inc.monthly_drawings || 0) + Number(inc.other_income || 0);
    const expenses = ["expenses_rent", "expenses_utilities", "expenses_food", "expenses_transport", "expenses_other"]
      .reduce((s, k) => s + Number(inc[k] || 0), 0);
    return { income, expenses, surplus: income - expenses };
  }, [inc]);

  const validateStep = () => {
    if (step === 0 && !v.max_monthly) return "Please tell us the maximum monthly payment you can afford.";
    if (step === 1 && (!p.first_names || !p.surname || !p.id_number || !p.mobile || !p.email || !p.address || !p.licence_number))
      return "Please complete the required personal and licence fields.";
    if (step === 2 && totals.income <= 0) return "Please enter your income figures.";
    if (step === 3) {
      if (!b.bank_name || !b.account_holder || !b.account_number) return "Please complete your banking details.";
      const missing = Object.entries(d).filter(([, val]) => !val).map(([k]) => k);
      if (missing.length) return "Please accept all declarations to submit.";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setStep((s) => Math.min(s + 1, STEP_TITLES.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const err = validateStep();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      const res = await submitApplication({
        full_name: `${p.first_names} ${p.surname}`.trim(),
        email: p.email,
        mobile: p.mobile,
        payload: { ...form, totals },
        referral_code: referral?.code || "",
      });
      setReference(res.reference);
      sessionStorage.removeItem("compcar_prequal");
      toast.success("Application submitted");
    } catch {
      toast.error("Submission failed. Please try again or call 010 534 9926.");
    } finally {
      setLoading(false);
    }
  };

  if (reference) {
    return (
      <div data-testid="apply-success" className="pt-40 pb-32 min-h-screen">
        <div className="w-full max-w-2xl mx-auto px-4 text-center">
          <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-8" />
          <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-4">Application received.</h1>
          <p className="text-slate-400 mb-8">Your reference is</p>
          <p className="font-display text-3xl font-black text-[#F57C00] tracking-widest mb-10" data-testid="application-reference">{reference}</p>
          <div className="text-left bg-[#122659] border border-sky-300/20 rounded-xl p-8 space-y-4 text-sm text-slate-300">
            <p><strong className="text-sky-300">1.</strong> Our team verifies your identity, licence, income and banking details.</p>
            <p><strong className="text-sky-300">2.</strong> We complete a lawful affordability and credit assessment.</p>
            <p><strong className="text-sky-300">3.</strong> If approved, you receive a written, vehicle-specific quotation and consumer agreement before signing.</p>
          </div>
          <a
            href={whatsappLink(`Hi CompCar Motors! I just submitted my rent-to-own application. Reference: ${reference}`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="apply-success-whatsapp"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fbe5b] text-white font-bold px-8 py-4 rounded-md transition-colors duration-300 mt-10"
          >
            Chat with us on WhatsApp
          </a>
          <p className="text-xs text-slate-500 mt-8">A confirmation has been emailed to you. Approval and vehicle availability are not guaranteed until the final agreement is signed.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="pt-32 pb-24 min-h-screen">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-sky-300 font-bold mb-4">Rent-to-own application</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-black text-white mb-4">
            Let's get you on the road.
          </h1>
          <p className="text-sm text-slate-400 mb-10">
            This is an application and affordability assessment — not an approval.
            No vehicle is reserved until written approval, a prescribed quotation
            and a signed consumer agreement.
          </p>
        </Reveal>

        <div className="flex items-center gap-2 mb-10" data-testid="step-indicator">
          {STEP_TITLES.map((t, i) => (
            <div key={t} className="flex-1">
              <div className={`h-1.5 rounded-full mb-2 transition-colors duration-300 ${i <= step ? "bg-[#F57C00]" : "bg-sky-300/15"}`} />
              <p className={`text-[10px] uppercase tracking-wider font-bold hidden sm:block ${i <= step ? "text-sky-300" : "text-slate-600"}`}>
                {i + 1}. {t}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-[#122659]/80 backdrop-blur-xl border border-sky-300/20 rounded-xl p-8 md:p-10" data-testid="application-form">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" {...stepMotion} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Preferred make" id="ap-make" testid="ap-input-make" value={v.make} onChange={sv("make")} placeholder="e.g. Toyota" />
                <Field label="Preferred model" id="ap-model" testid="ap-input-model" value={v.model} onChange={sv("model")} placeholder="e.g. Corolla Quest" />
                <Field label="Year range" id="ap-year" testid="ap-input-year" value={v.year_range} onChange={sv("year_range")} placeholder="e.g. 2020–2023" />
                <Field label="Max vehicle price (R)" id="ap-maxprice" testid="ap-input-maxprice" type="number" min="0" value={v.max_price} onChange={sv("max_price")} placeholder="200000" />
                <Field label="Preferred term" id="ap-term" testid="ap-select-term" value={v.term} onChange={sv("term")} options={[{ value: "48", label: "48 months" }, { value: "60", label: "60 months" }]} />
                <Field label="Max affordable monthly payment (R)" id="ap-maxmonthly" testid="ap-input-maxmonthly" type="number" min="0" required value={v.max_monthly} onChange={sv("max_monthly")} placeholder="6000" />
                <Field label="Available ownership deposit (R)" id="ap-owndep" testid="ap-input-owndep" type="number" min="0" value={v.ownership_deposit} onChange={sv("ownership_deposit")} placeholder="10000" />
                <Field label="Available security deposit (R)" id="ap-secdep" testid="ap-input-secdep" type="number" min="0" value={v.security_deposit} onChange={sv("security_deposit")} placeholder="5000" />
                <Field label="Preferred collection date" id="ap-collect" testid="ap-input-collect" type="date" value={v.collection_date} onChange={sv("collection_date")} />
                <Field label="Alternative vehicle acceptable?" id="ap-alt" testid="ap-select-alt" value={v.alternative_ok} onChange={sv("alternative_ok")} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" {...stepMotion} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Title" id="ap-title" testid="ap-select-title" value={p.title} onChange={sp("title")} options={["Mr", "Ms", "Mrs", "Dr"].map((x) => ({ value: x, label: x }))} />
                <div />
                <Field label="Full first names" id="ap-firstnames" testid="ap-input-firstnames" required value={p.first_names} onChange={sp("first_names")} placeholder="Thandi Grace" />
                <Field label="Surname" id="ap-surname" testid="ap-input-surname" required value={p.surname} onChange={sp("surname")} placeholder="Mokoena" />
                <Field label="SA ID / passport number" id="ap-id" testid="ap-input-id" required value={p.id_number} onChange={sp("id_number")} placeholder="9001014800082" />
                <Field label="Date of birth" id="ap-dob" testid="ap-input-dob" type="date" value={p.dob} onChange={sp("dob")} />
                <Field label="Mobile" id="ap-mobile" testid="ap-input-mobile" required value={p.mobile} onChange={sp("mobile")} placeholder="082 000 0000" />
                <Field label="Email" id="ap-email" testid="ap-input-email" type="email" required value={p.email} onChange={sp("email")} placeholder="you@example.co.za" />
                <div className="sm:col-span-2">
                  <Field label="Residential address" id="ap-address" testid="ap-input-address" required value={p.address} onChange={sp("address")} placeholder="12 Protea Street, Jansen Park, Boksburg" />
                </div>
                <Field label="Period at address" id="ap-period" testid="ap-input-period" value={p.period_at_address} onChange={sp("period_at_address")} placeholder="e.g. 2 years" />
                <Field label="Overnight parking" id="ap-parking" testid="ap-select-parking" value={p.parking} onChange={sp("parking")} options={["garage", "secure yard", "street", "other"].map((x) => ({ value: x, label: x[0].toUpperCase() + x.slice(1) }))} />
                <Field label="Driver's licence number" id="ap-licence" testid="ap-input-licence" required value={p.licence_number} onChange={sp("licence_number")} />
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Code" id="ap-liccode" testid="ap-input-liccode" value={p.licence_code} onChange={sp("licence_code")} placeholder="B" />
                  <Field label="Expiry" id="ap-licexp" testid="ap-input-licexp" type="date" value={p.licence_expiry} onChange={sp("licence_expiry")} />
                </div>
                <Field label="Est. commute km / day" id="ap-commute" testid="ap-input-commute" type="number" min="0" value={p.commute_km} onChange={sp("commute_km")} placeholder="40" />
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" {...stepMotion} className="space-y-6">
                <div className="flex gap-3" data-testid="employment-toggle">
                  {["employed", "self-employed"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`employment-${t}`}
                      onClick={() => setForm((f) => ({ ...f, income: { ...f.income, employment_type: t } }))}
                      className={`flex-1 text-sm font-bold px-4 py-3 rounded-md border transition-colors duration-200 ${
                        inc.employment_type === t ? "bg-[#F57C00] border-[#F57C00] text-white" : "border-sky-300/30 text-slate-300"
                      }`}
                    >
                      {t === "employed" ? "Employed" : "Self-Employed"}
                    </button>
                  ))}
                </div>

                {inc.employment_type === "employed" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Employer name" id="ap-employer" testid="ap-input-employer" value={inc.employer} onChange={si("employer")} />
                    <Field label="Job title" id="ap-jobtitle" testid="ap-input-jobtitle" value={inc.job_title} onChange={si("job_title")} />
                    <Field label="Employment start date" id="ap-empstart" testid="ap-input-empstart" type="date" value={inc.employment_start} onChange={si("employment_start")} />
                    <Field label="Net monthly salary (R)" id="ap-netsalary" testid="ap-input-netsalary" type="number" min="0" required value={inc.net_salary} onChange={si("net_salary")} placeholder="18000" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Business name" id="ap-business" testid="ap-input-business" value={inc.business_name} onChange={si("business_name")} />
                    <Field label="Avg monthly turnover (R)" id="ap-turnover" testid="ap-input-turnover" type="number" min="0" value={inc.business_turnover} onChange={si("business_turnover")} />
                    <Field label="Avg monthly drawings / salary (R)" id="ap-drawings" testid="ap-input-drawings" type="number" min="0" required value={inc.monthly_drawings} onChange={si("monthly_drawings")} placeholder="15000" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Other verified income (R)" id="ap-otherincome" testid="ap-input-otherincome" type="number" min="0" value={inc.other_income} onChange={si("other_income")} placeholder="0" />
                  <Field label="Housing: rent / bond (R)" id="ap-exprent" testid="ap-input-exprent" type="number" min="0" value={inc.expenses_rent} onChange={si("expenses_rent")} placeholder="4500" />
                  <Field label="Utilities & household (R)" id="ap-exputil" testid="ap-input-exputil" type="number" min="0" value={inc.expenses_utilities} onChange={si("expenses_utilities")} placeholder="1500" />
                  <Field label="Food & groceries (R)" id="ap-expfood" testid="ap-input-expfood" type="number" min="0" value={inc.expenses_food} onChange={si("expenses_food")} placeholder="3000" />
                  <Field label="Transport & other commitments (R)" id="ap-exptrans" testid="ap-input-exptrans" type="number" min="0" value={inc.expenses_transport} onChange={si("expenses_transport")} placeholder="1200" />
                  <Field label="All other expenses (R)" id="ap-expother" testid="ap-input-expother" type="number" min="0" value={inc.expenses_other} onChange={si("expenses_other")} placeholder="2000" />
                </div>

                <div className="grid grid-cols-3 gap-4" data-testid="affordability-summary">
                  <div className="bg-[#0B1F4E] rounded-lg p-4 border border-sky-300/20 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Income</p>
                    <p className="font-display text-lg font-black text-white" data-testid="summary-income">{zar(totals.income)}</p>
                  </div>
                  <div className="bg-[#0B1F4E] rounded-lg p-4 border border-sky-300/20 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Expenses</p>
                    <p className="font-display text-lg font-black text-white" data-testid="summary-expenses">{zar(totals.expenses)}</p>
                  </div>
                  <div className="bg-[#0B1F4E] rounded-lg p-4 border border-sky-300/20 text-center">
                    <p className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1">Surplus</p>
                    <p className={`font-display text-lg font-black ${totals.surplus >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid="summary-surplus">{zar(totals.surplus)}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" {...stepMotion} className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Bank name" id="ap-bank" testid="ap-input-bank" required value={b.bank_name} onChange={sb("bank_name")} placeholder="e.g. Capitec" />
                  <Field label="Account holder" id="ap-accholder" testid="ap-input-accholder" required value={b.account_holder} onChange={sb("account_holder")} />
                  <Field label="Account type" id="ap-acctype" testid="ap-select-acctype" value={b.account_type} onChange={sb("account_type")} options={["cheque", "savings", "transmission"].map((x) => ({ value: x, label: x[0].toUpperCase() + x.slice(1) }))} />
                  <Field label="Account number" id="ap-accnum" testid="ap-input-accnum" required value={b.account_number} onChange={sb("account_number")} />
                  <Field label="Branch code" id="ap-branch" testid="ap-input-branch" value={b.branch_code} onChange={sb("branch_code")} />
                  <Field label="Preferred debit-order day" id="ap-debitday" testid="ap-input-debitday" value={b.debit_day} onChange={sb("debit_day")} placeholder="25" />
                </div>

                <div className="space-y-4" data-testid="declarations-block">
                  <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold">Declarations</p>
                  {[
                    ["private_use", "The vehicle will be used for private personal and ordinary commuting use only.", "decl-private-use"],
                    ["no_ehailing", "The vehicle will not be used for e-hailing, courier, delivery, taxi or any carriage for reward, and will not be sub-rented.", "decl-no-ehailing"],
                    ["tracker_consent", "I consent to an approved tracker and lawful mileage/location monitoring if approved.", "decl-tracker"],
                    ["popia_consent", "I consent to CompCar Motors processing my information under POPIA for identity, affordability, credit and verification purposes.", "decl-popia"],
                    ["truthful", "I declare that the information provided is complete and true, and I understand approval is not guaranteed.", "decl-truthful"],
                  ].map(([key, text, testid]) => (
                    <label key={key} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        data-testid={testid}
                        checked={d[key]}
                        onChange={sd(key)}
                        className="mt-1 w-4 h-4 accent-[#F57C00]"
                      />
                      <span className="text-sm text-slate-300 leading-relaxed group-hover:text-white transition-colors">{text}</span>
                    </label>
                  ))}
                </div>

                <div className="space-y-4" data-testid="documents-block">
                  <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold">Supporting documents (optional)</p>
                  <p className="text-xs text-slate-500 -mt-2">Uploading now speeds up verification. PDF or a clear photo, max 10MB each.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      ["id_copy", "ID copy"],
                      ["licence_copy", "Driver's licence"],
                      ["proof_of_income", "Payslip / proof of income"],
                    ].map(([key, label]) => (
                      <label
                        key={key}
                        data-testid={`doc-upload-${key}`}
                        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md px-4 py-5 text-xs font-bold cursor-pointer transition-colors duration-200 text-center ${
                          form.documents[key]
                            ? "border-emerald-400/50 text-emerald-300"
                            : docUploading === key
                              ? "border-sky-300/30 text-slate-500"
                              : "border-sky-300/30 text-sky-300 hover:border-sky-300 hover:text-white"
                        }`}
                      >
                        {docUploading === key ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : form.documents[key] ? (
                          <CheckCircle2 size={18} />
                        ) : (
                          <Upload size={18} />
                        )}
                        {docUploading === key ? "Uploading…" : form.documents[key] ? `${label} uploaded` : `Upload ${label.toLowerCase()}`}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,application/pdf"
                          data-testid={`doc-input-${key}`}
                          className="hidden"
                          onChange={handleDoc(key)}
                          disabled={docUploading !== ""}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-between items-center mt-10 pt-6 border-t border-sky-300/10">
            <button
              type="button"
              data-testid="apply-back-btn"
              onClick={back}
              disabled={step === 0}
              className="inline-flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < STEP_TITLES.length - 1 ? (
              <button
                type="button"
                data-testid="apply-next-btn"
                onClick={next}
                className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-7 py-3.5 rounded-md transition-colors duration-300"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                data-testid="apply-submit-btn"
                onClick={submit}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#F57C00] hover:bg-[#E67300] disabled:opacity-60 text-white text-sm font-bold px-7 py-3.5 rounded-md transition-colors duration-300"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Submitting…" : "Submit application"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
